import type { CollectionChildren } from "@react-types/shared";
import type { PropsWithChildren, ReactElement } from "react";
import { Children, useRef } from "react";
import { type AriaTabListOptions, type AriaTabListProps, useTabList as useAriaTabList } from "react-aria";
import type { SingleSelectListProps, TabListState } from "react-stately";
import { Item, useSingleSelectListState, useTabListState } from "react-stately";
/**
 * The main constructor of Tabs. Will set and keep the Tabs state.
 * @param props The children are a list of <TabItem>
 */
const useTabList = <T extends HTMLElement>(props: PropsWithChildren<AriaTabListProps<ReactElement>>) => {
  const extractedChildren: CollectionChildren<ReactElement> = [];

  // Take care with returned keys from React, it will add a ".$" before the key string.
  Children.forEach(props.children, (child) => {
    if (child) {
      extractedChildren.push(<Item {...(child as ReactElement)?.props} key={(child as ReactElement)?.key} />);
    }
  });

  const updatedProps = { ...props, children: extractedChildren };
  const state = useTabListState(updatedProps);
  const ref = useRef<T>(null);
  const { tabListProps } = useAriaTabList(updatedProps as AriaTabListOptions<unknown>, state, ref);

  return { ref, state, tabListProps };
};

/**
 * Also a main constructor of Tabs. Will set and keep the Tabs state.
 * But allow starting with no active key and also deactivating a selection.
 * @param props The children are a list of <TabItem>
 */
const useNoSelectionTabList = <T extends HTMLElement>(
  props: PropsWithChildren<SingleSelectListProps<ReactElement>>,
) => {
  const extractedChildren: CollectionChildren<ReactElement> = [];

  // Take care with returned keys from React, it will add a ".$" before the key string.
  Children.forEach(props.children, (child) => {
    extractedChildren.push(<Item {...(child as ReactElement)?.props} key={(child as ReactElement).key} />);
  });

  const updatedProps = { ...props, children: extractedChildren };
  // We don't use the usual useTabListState because we need to be able to have no selection
  const state = useSingleSelectListState(updatedProps);
  const ref = useRef<T>(null);
  const { tabListProps } = useAriaTabList(updatedProps, state as TabListState<unknown>, ref);

  return { ref, state, tabListProps };
};

export { useNoSelectionTabList, useTabList };
